import { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Paperclip, X, Loader2, FileText, Image as ImageIcon, Search, ArrowLeft, Check, CheckCheck, MessagesSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { EmojiPicker } from '../components/EmojiPicker';
import { ChatAttachmentView } from '../components/ChatAttachmentView';
import { useCare, useLiveCare, errorText } from './CareContext';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf', 'text/plain'];

const initials = (label = '?') => label.trim().charAt(0).toUpperCase() || '?';

const stampLabel = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const dayLabel = iso => {
  const d = new Date(iso + 'T00:00:00');
  const today = new Date();
  const diff = Math.round((new Date(today.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: diff > 300 ? 'numeric' : undefined });
};

const ThreadRow = ({ thread, label, active, unread, onOpen }) => (
  <button type="button" onClick={() => onOpen(thread.thread_id)}
    className={`wa-row ${active ? 'wa-row-active' : ''}`} data-testid={`care-chat-thread-${thread.thread_id}`}>
    <span className="wa-avatar" aria-hidden="true">{initials(label)}</span>
    <span className="wa-row-main">
      <span className="wa-row-top">
        <span className="wa-row-name" data-testid={`care-chat-row-name-${thread.thread_id}`}>{label}</span>
        <span className="wa-row-time">{stampLabel(thread.last_message_at)}</span>
      </span>
      <span className="wa-row-bottom">
        <span className="wa-row-preview">
          {thread.last_message_sender === 'staff' && <Check size={13} className="shrink-0 opacity-60" />}
          {thread.last_message_preview || 'No messages yet'}
        </span>
        {unread > 0 && <span className="wa-row-badge" data-testid={`care-chat-unread-${thread.thread_id}`}>{unread > 99 ? '99+' : unread}</span>}
      </span>
    </span>
  </button>
);

const ChatMessageBubble = ({ message, mine, showDaySeparator, dateLabel, api }) => (
  <>
    {showDaySeparator && (
      <p className="wa-day" data-testid={`care-chat-day-${dateLabel}`}>{dayLabel(dateLabel)}</p>
    )}
    <article className={`wa-bubble ${mine ? 'wa-bubble-mine' : 'wa-bubble-theirs'}`} data-testid={`care-message-${message.message_id}`}>
      {message.body && (
        <p className="wa-bubble-text" data-testid={`care-message-body-${message.message_id}`}>{message.body}</p>
      )}
      {message.attachments?.map((att, i) => (
        <ChatAttachmentView key={i} attachment={att} api={api} testId={`care-message-attachment-${message.message_id}-${i}`} />
      ))}
      <span className="wa-meta">
        {new Date(message.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        {mine && (message.read_at
          ? <CheckCheck size={14} className="wa-tick wa-tick-read" data-testid={`care-message-read-${message.message_id}`} />
          : <Check size={14} className="wa-tick" />)}
      </span>
      {message.reactions?.patient && (
        <span className="wa-reaction" data-testid={`care-message-reaction-${message.message_id}`} title="Patient reacted">
          {message.reactions.patient}
        </span>
      )}
    </article>
  </>
);

const ChatMessageList = ({ messages, patientMode, api }) => {
  const scrollRef = useRef(null);
  useEffect(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [messages]);
  let previousDay = '';
  return (
    <div ref={scrollRef} className="wa-canvas" data-testid="care-chat-messages">
      {messages?.map(message => {
        const dateLabel = message.created_at.slice(0, 10);
        const showDaySeparator = dateLabel !== previousDay;
        previousDay = dateLabel;
        return (
          <ChatMessageBubble
            key={message.message_id}
            message={message}
            mine={message.sender === (patientMode ? 'patient' : 'staff')}
            showDaySeparator={showDaySeparator}
            dateLabel={dateLabel}
            api={api}
          />
        );
      })}
    </div>
  );
};

// Polls the typing-status endpoint every 2.5s while an active thread is open.
const useTypingStatus = (api, thread, view) => {
  const [typingStatus, setTypingStatus] = useState({ typing_patient: false, typing_staff: false });
  useEffect(() => {
    if (!thread || view !== 'active') { setTypingStatus({ typing_patient: false, typing_staff: false }); return; }
    let cancelled = false;
    const poll = () => api.get(`/chat/${thread}/typing`).then(r => { if (!cancelled) setTypingStatus(r.data); }).catch(() => {});
    poll();
    const timer = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(timer); };
  }, [thread, view, api]);
  return typingStatus;
};

export const CareChatView = ({ patientId }) => {
  const { patientMode, api, branch } = useCare();
  const { data, refresh } = useLiveCare('/chat/threads', { threads: [] });
  const [thread, setThread] = useState('');
  const [view, setView] = useState('active');
  const [day, setDay] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [paneOpen, setPaneOpen] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const conversationPath = thread ? `/chat/${thread}?view=${view}${day ? '&day=' + day : ''}` : '/chat/threads';
  const { data: conversation, error, refresh: reload } = useLiveCare(conversationPath, { messages: [] });
  const typingStatus = useTypingStatus(api, thread, view);
  const lastTypingSentRef = useRef(0);

  // Auto-select the relevant thread once the thread list has loaded.
  useEffect(() => {
    if (thread || !data.threads.length) return;
    const match = patientId
      ? data.threads.find(t => t.patient_user_id === patientId)
      : (data.threads.find(t => t.tenant_id === branch) || data.threads[0]);
    if (match) setThread(match.thread_id);
  }, [data, branch, thread, patientId]);

  const active = data.threads.find(t => t.thread_id === thread);
  const threadLabel = t => (patientMode ? t.tenant_id : (t.patient_name || t.patient_user_id));

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.threads;
    return data.threads.filter(t => `${threadLabel(t)} ${t.last_message_preview || ''}`.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.threads, query, patientMode]);

  // Mark visible unread messages as read. Deliberately omits `api`/`refresh`
  // from deps — useLiveCare returns a fresh `refresh` identity every render,
  // which would otherwise re-fire this POST on every render.
  useEffect(() => {
    if (thread && conversation.messages?.length && document.visibilityState === 'visible') {
      api.post(`/chat/${thread}/read`, { message_ids: conversation.messages.map(m => m.message_id) })
        .then(refresh).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread, conversation.messages?.length]);

  const notifyTyping = () => {
    if (!thread) return;
    const nowTs = Date.now();
    if (nowTs - lastTypingSentRef.current < 1500) return;
    lastTypingSentRef.current = nowTs;
    api.post(`/chat/${thread}/typing`).catch(() => {});
  };

  const handleFileSelect = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { toast.error('Unsupported file type. Use JPEG, PNG, WebP, GIF, PDF, or TXT'); return; }
    if (file.size > MAX_FILE_BYTES) { toast.error('File too large — maximum 10 MB'); return; }
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/chat/attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPendingAttachment(res.data);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const send = async e => {
    e.preventDefault();
    if (!text.trim() && !pendingAttachment) return;
    setBusy(true);
    try {
      const payload = { tenant_id: branch, thread_id: thread, body: text.trim() };
      if (pendingAttachment) payload.attachments = [pendingAttachment];
      const result = await api.post('/chat', payload);
      setThread(result.data.thread_id);
      setText(''); setPendingAttachment(null);
      await refresh();
      await reload();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const sendOnEnter = e => { if (e.key === 'Enter' && !e.shiftKey) send(e); };
  const openThread = id => { setThread(id); setDay(''); setPaneOpen(true); };

  const otherTyping = view === 'active' && (patientMode ? typingStatus.typing_staff : typingStatus.typing_patient);
  const canSend = view === 'active' && (patientMode || active);
  const showList = !patientId;

  return (
    <div className={`wa-shell ${showList ? '' : 'wa-shell-single'} ${paneOpen ? 'wa-pane-open' : ''}`} data-testid="care-chat-shell">
      {showList && (
        <aside className="wa-list" data-testid="care-chat-list">
          <header className="wa-list-head">
            <h2 className="flex items-center gap-2"><MessagesSquare size={17} />Conversations</h2>
            <div className="wa-search">
              <Search size={15} />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search conversations"
                aria-label="Search conversations" data-testid="care-chat-search" />
            </div>
          </header>
          <div className="wa-list-scroll">
            {rows.map(t => (
              <ThreadRow key={t.thread_id} thread={t} label={threadLabel(t)} active={t.thread_id === thread}
                unread={patientMode ? t.unread_by_patient : t.unread_by_staff} onOpen={openThread} />
            ))}
            {!rows.length && <p className="wa-list-empty" data-testid="care-chat-list-empty">No conversations yet.</p>}
          </div>
        </aside>
      )}

      <section className="wa-pane" data-testid="care-chat-pane">
        <header className="wa-pane-head">
          {showList && (
            <button type="button" className="wa-back" onClick={() => setPaneOpen(false)} aria-label="Back to conversations" data-testid="care-chat-back">
              <ArrowLeft size={18} />
            </button>
          )}
          <span className="wa-avatar" aria-hidden="true">{initials(active ? threadLabel(active) : 'P')}</span>
          <div className="wa-pane-id">
            <strong data-testid="care-chat-pane-title">{active ? threadLabel(active) : (patientMode ? 'New conversation' : 'Select a conversation')}</strong>
            {otherTyping ? (
              <em data-testid="care-chat-typing-indicator">{patientMode ? 'typing…' : 'patient is typing…'}</em>
            ) : (
              <span>{active ? active.tenant_id : (patientMode ? branch || '' : '')}</span>
            )}
          </div>
          <select className="wa-view-select" value={view} onChange={e => setView(e.target.value)} data-testid="care-chat-view" aria-label="Conversation view">
            <option value="active">Active · 72h</option>
            <option value="archive">Archive</option>
          </select>
        </header>

        {view === 'archive' && (
          <div className="wa-archive-bar">
            <Input aria-label="Archive date" type="date" value={day} onChange={e => setDay(e.target.value)} data-testid="care-chat-archive-day" />
          </div>
        )}

        {error && thread && <p role="alert" className="wa-alert" data-testid="care-chat-error">{error}</p>}
        {patientId && !thread && <p className="care-empty" data-testid="care-chat-no-thread">No conversation with this customer yet.</p>}

        <ChatMessageList messages={conversation.messages} patientMode={patientMode} api={api} />

        {canSend ? (
          <form className="wa-composer" onSubmit={send}>
            {pendingAttachment && (
              <div className="wa-pending" data-testid="care-chat-pending-attachment">
                {pendingAttachment.content_type?.startsWith('image/') ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                <span className="truncate max-w-[220px]">{pendingAttachment.filename}</span>
                <span>({(pendingAttachment.size / 1024).toFixed(1)} KB)</span>
                <button type="button" onClick={() => setPendingAttachment(null)} className="ml-auto text-red-500 hover:text-red-700" data-testid="care-chat-remove-attachment">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="wa-composer-row">
              <EmojiPicker testId="care-chat-emoji" onSelect={emoji => setText(t => t + emoji)} />
              {!patientMode && (
                <>
                  <input ref={fileInputRef} type="file" accept={ALLOWED_TYPES.join(',')} className="hidden" onChange={handleFileSelect} data-testid="care-chat-file-input" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment || !!pendingAttachment} title="Attach a file" className="shrink-0" data-testid="care-chat-attach-btn">
                    {uploadingAttachment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  </Button>
                </>
              )}
              <Textarea
                rows={1}
                value={text}
                onChange={e => { setText(e.target.value); notifyTyping(); }}
                onKeyDown={sendOnEnter}
                maxLength={4000}
                placeholder={patientMode ? 'Message to the pharmacist' : 'Type a message'}
                className="wa-input"
                data-testid="care-chat-input"
              />
              <button type="submit" className="wa-send" disabled={busy || (!text.trim() && !pendingAttachment)} aria-label="Send message" data-testid="care-chat-send">
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </form>
        ) : (
          <div className="wa-composer wa-composer-locked" data-testid="care-chat-locked">
            {view === 'archive' ? 'Archived conversation · read-only' : 'Select a conversation to reply'}
          </div>
        )}
      </section>
    </div>
  );
};
